const TRACKJS_DOMAIN = "trackjs.com"

const isTrackJSOrigin = (source: string): boolean => {
  try {
    const { hostname } = new URL(source)
    return hostname === TRACKJS_DOMAIN || hostname.endsWith(`.${TRACKJS_DOMAIN}`)
  } catch {
    return false
  }
}

const blockedSource = (blockedURI: string): string => {
  if (!blockedURI) return "unknown"

  // Browsers use values such as "inline" and "eval" when no URL was blocked.
  if (/^[a-z-]+$/i.test(blockedURI)) return blockedURI

  try {
    const url = new URL(blockedURI, document.baseURI)
    if (["http:", "https:", "ws:", "wss:"].includes(url.protocol)) return url.origin
    return url.protocol
  } catch {
    return "unknown"
  }
}

const sourceFileOrigin = (sourceFile: string): string | undefined => {
  if (!sourceFile) return undefined

  try {
    const url = new URL(sourceFile, document.baseURI)
    return url.origin === "null" ? url.protocol : url.origin
  } catch {
    return undefined
  }
}

/** Report each distinct CSP violation to TrackJS once per page load. */
export const installCSPViolationReporting = (track: (message: string) => void): (() => void) => {
  // Bytespider injects third-party scripts that our CSP correctly blocks, creating noisy reports.
  if (/\bBytespider\b/i.test(navigator.userAgent)) return () => undefined

  const reportedViolations = new Set<string>()

  const onViolation = (event: SecurityPolicyViolationEvent) => {
    const source = blockedSource(event.blockedURI)
    const sourceOrigin = sourceFileOrigin(event.sourceFile)

    // If CSP ever blocks TrackJS itself, attempting to report that failure could recurse.
    if (isTrackJSOrigin(source)) return

    const sourceContext = sourceOrigin ? ` from ${sourceOrigin}` : ""
    const message = `CSP violation [${event.disposition}]: ${event.effectiveDirective} blocked ${source}${sourceContext}`
    if (reportedViolations.has(message)) return

    reportedViolations.add(message)
    track(message)
  }

  document.addEventListener("securitypolicyviolation", onViolation)
  return () => document.removeEventListener("securitypolicyviolation", onViolation)
}
